import { generatePath, Link, useLocation, useMatch } from 'react-router';
import classNames from 'classnames';
import { IconReload } from '@tabler/icons-react';
import {
    NavigationSearchCompany,
    NavigationSearchSite,
} from 'types/Navigation';
import { getIdFromItem, normalizeRouteParams } from './helpers';
import { COMPANY_ROUTE_PATH, SITE_ROUTE_PATH } from 'utils/constants';
import { companyNavigationData } from 'pages/Company/companyNavigationData';
import type { ResultsType } from './SearchDropdownResults';
import { siteNavigationData } from 'pages/Site/siteNavigationData';
import { getSharedQueryParams } from 'utils/queryParams';

interface SearchDropdownLinkProps {
    item: NavigationSearchCompany | NavigationSearchSite;
    type: ResultsType;
    index: number;
    selectedIndex?: number;
    onSelected: () => void;
}

export const SearchDropdownLink = ({
    item,
    type,
    index,
    selectedIndex,
    onSelected,
}: SearchDropdownLinkProps) => {
    const location = useLocation();
    const isCompanyLink = type === 'companies';
    const isSelected = index === selectedIndex;
    const id = getIdFromItem(type, item);
    const idKey = isCompanyLink ? 'companyId' : 'siteId';

    // casting to string to sidestep typing requirements from useMatch/generatePath
    const routePattern: string = isCompanyLink
        ? COMPANY_ROUTE_PATH
        : SITE_ROUTE_PATH;
    const match = useMatch(routePattern);

    const pathname = match
        ? generatePath(
              routePattern,
              isCompanyLink
                  ? normalizeRouteParams(match.params, idKey, id)
                  : {
                        ...normalizeRouteParams(match.params, idKey, id),
                        detail: null,
                    }
          )
        : generatePath(
              isCompanyLink
                  ? companyNavigationData.options.link
                  : siteNavigationData.overview.link,
              {
                  [idKey]: id,
              }
          );

    const search = getSharedQueryParams(location.search);
    const to = match ? { pathname, search } : pathname;
    const state = isCompanyLink ? undefined : { site: item };

    return (
        <div role="option" aria-selected={isSelected}>
            <Link
                to={to}
                state={state}
                onClick={onSelected}
                tabIndex={-1}
                className={classNames(
                    '!text-primary text-sm py-1.5 px-2 hover:bg-gray-100 flex gap-2 items-center scroll-mt-8',
                    { 'bg-gray-100': isSelected }
                )}
            >
                {type === 'recent' && (
                    <div className="w-4 shrink-0">
                        <IconReload size={16} />
                    </div>
                )}
                <div>{item.name}</div>
            </Link>
        </div>
    );
};
